import React, { useEffect, useMemo, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import { CircleCheck, Mail, Save, UserPlus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from '@/components/ui/toast';
import { cn } from '@/lib/utils';
import { OdrCandidateAffidavits } from './OdrCandidateAffidavits';
import { OdrWorkflowStatusIcon, getOdrWorkflowPresentation } from './odrWorkflowPresentation';

export type OdrCandidateTaskType = 'charges' | 'assign-clinician' | 'clinical-review' | 'affidavit' | 'attorney-email' | 'add-to-dd';

export type OdrTaskCandidateRow = {
  id: number | string;
  cells: Array<{ key: string; value: unknown }>;
};

type OdrClinicianOption = { id: number; username: string; name?: string | null };
type CandidateMutationResult = { success?: boolean | null };
type ParticipantMatch = {
  participantId: number;
  odrParticipantId?: number | null;
  program?: string | null;
  reason?: string | null;
};
type ParticipantPreflight = { canCreate?: boolean; missingFields?: string[]; matches?: ParticipantMatch[] };
type AttorneyEmailDraft = { referrerName: string; email: string; subject: string; body: string };

const ATTORNEY_EMAIL_DRAFT = gql`
  query OdrCandidateAttorneyEmail($candidateId: Int!) {
    odrCandidateAttorneyEmail(candidateId: $candidateId) { referrerName email subject body }
  }
`;

const SEND_ATTORNEY_EMAIL = gql`
  mutation SendOdrCandidateAttorneyEmail($candidateId: Int!, $body: String!) {
    sendOdrCandidateAttorneyEmail(candidateId: $candidateId, body: $body) { success id }
  }
`;

const MARK_ATTORNEY_EMAILED = gql`
  mutation MarkOdrCandidateAttorneyEmailed($candidateId: Int!) {
    updateCandidateField(component: "odr", id: $candidateId, field: "emailed_pd", value: "y") { success id }
  }
`;

const CANDIDATE_TASK_DETAILS = gql`
  query OdrCandidateTaskDetails($candidateId: Int!) {
    candidateById(component: "odr", id: $candidateId) {
      id
      cells { key value }
    }
  }
`;

const ODR_CANDIDATE_CLINICIANS = gql`
  query OdrCandidateTaskClinicians {
    odrCandidateClinicians { id username name }
  }
`;

const REVIEW_ODR_CANDIDATE_CHARGES = gql`
  mutation ReviewOdrCandidateCharges($candidateId: Int!, $charges: String!) {
    reviewOdrCandidateCharges(candidateId: $candidateId, charges: $charges) {
      success
      id
      value
    }
  }
`;

const ASSIGN_ODR_CANDIDATE_CLINICIAN = gql`
  mutation AssignOdrCandidateClinician($candidateId: Int!, $clinicianUserId: Int!) {
    assignOdrCandidateClinician(candidateId: $candidateId, clinicianUserId: $clinicianUserId) {
      success
      id
      value
    }
  }
`;

const SEND_ODR_CANDIDATE_CLINICIAN_ASSIGNMENT_EMAIL = gql`
  mutation SendOdrCandidateClinicianAssignmentEmail($candidateId: Int!) {
    sendOdrCandidateClinicianAssignmentEmail(candidateId: $candidateId) {
      success
      id
      value
    }
  }
`;

const COMPLETE_ODR_CANDIDATE_CLINICAL_REVIEW = gql`
  mutation CompleteOdrCandidateClinicalReview($candidateId: Int!) {
    completeOdrCandidateClinicalReview(candidateId: $candidateId) {
      success
      id
      value
    }
  }
`;

const SAVE_ODR_CANDIDATE_CLINICAL_NOTES = gql`
  mutation SaveOdrCandidateClinicalNotes($candidateId: Int!, $notes: String!) {
    updateCandidate(component: "odr", id: $candidateId, values: [{ key: "notes", value: $notes }]) {
      success
      id
    }
  }
`;

const SAVE_ODR_CANDIDATE_DEMOGRAPHICS = gql`
  mutation SaveOdrCandidateDemographics($candidateId: Int!, $sex: String!, $dob: String!) {
    updateCandidate(component: "odr", id: $candidateId, values: [{ key: "sex", value: $sex }, { key: "dob", value: $dob }]) {
      success
      id
    }
  }
`;

const CANDIDATE_PARTICIPANT_PREFLIGHT = gql`
  query OdrCandidateParticipantPreflight($candidateId: Int!) {
    candidateParticipantPreflight(candidateId: $candidateId) {
      canCreate
      missingFields
      matches { participantId odrParticipantId program reason }
    }
  }
`;

const CREATE_ODR_PARTICIPANT = gql`
  mutation CreateOdrParticipantFromCandidate($candidateId: Int!, $acknowledgeMatches: Boolean, $matchedParticipantId: Int) {
    createOdrParticipantFromCandidate(candidateId: $candidateId, acknowledgeMatches: $acknowledgeMatches, matchedParticipantId: $matchedParticipantId) {
      success
      participantId
      warnings
    }
  }
`;

const TASK_TITLES: Record<OdrCandidateTaskType, string> = {
  charges: 'Review charges',
  'assign-clinician': 'Assign clinician',
  'clinical-review': 'Clinical review',
  affidavit: 'Prepare affidavit',
  'attorney-email': 'Attorney Email',
  'add-to-dd': 'Add Client to DD',
};

const TASK_STAGES: Record<OdrCandidateTaskType, string[]> = {
  charges: ['received'],
  'assign-clinician': ['ready_to_assign_clinician', 'clinician_assigned'],
  'clinical-review': ['assigned_to_clinician'],
  affidavit: ['clinician_review_complete', 'participant_created', 'affidavit_generated', 'affidavit_sent'],
  'attorney-email': ['clinician_review_complete', 'participant_created', 'affidavit_generated', 'affidavit_sent', 'closed'],
  'add-to-dd': ['affidavit_sent'],
};

const cellsToValues = (row?: OdrTaskCandidateRow | null): Record<string, string> => {
  const values: Record<string, string> = {};
  for (const cell of row?.cells || []) {
    values[cell.key] = cell.value === null || cell.value === undefined ? '' : String(cell.value);
  }
  return values;
};

const numericValue = (value: unknown): number => Number(value || 0) || 0;

type OdrCandidateTaskDialogProps = {
  candidateId: number;
  task: OdrCandidateTaskType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onChanged: () => void | Promise<void>;
  onCompleted: () => void | Promise<void>;
};

export const OdrCandidateTaskDialog = ({
  candidateId,
  task,
  open,
  onOpenChange,
  onChanged,
  onCompleted,
}: OdrCandidateTaskDialogProps) => {
  const apolloClient = useApolloClient();
  const [charges, setCharges] = useState('');
  const [notes, setNotes] = useState('');
  const [clinicianUserId, setClinicianUserId] = useState('');
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [sex, setSex] = useState('');
  const [dob, setDob] = useState('');
  const [addingToDd, setAddingToDd] = useState(false);
  const [participantMatches, setParticipantMatches] = useState<ParticipantMatch[]>([]);
  const [participantChoice, setParticipantChoice] = useState('');
  const [attorneyBody, setAttorneyBody] = useState('');
  const { data, loading, error, refetch } = useQuery<{ candidateById: OdrTaskCandidateRow | null }>(CANDIDATE_TASK_DETAILS, {
    variables: { candidateId },
    skip: !open || !candidateId,
    fetchPolicy: 'network-only',
  });
  const { data: cliniciansData, loading: cliniciansLoading, error: cliniciansError } = useQuery<{ odrCandidateClinicians: OdrClinicianOption[] }>(ODR_CANDIDATE_CLINICIANS, {
    skip: !open || task !== 'assign-clinician',
    fetchPolicy: 'network-only',
  });
  const { data: attorneyData, loading: attorneyLoading, error: attorneyError } = useQuery<{ odrCandidateAttorneyEmail: AttorneyEmailDraft }>(ATTORNEY_EMAIL_DRAFT, {
    variables: { candidateId },
    skip: !open || task !== 'attorney-email',
    fetchPolicy: 'network-only',
  });
  const [sendAttorneyEmail, { loading: sendingAttorneyEmail }] = useMutation<{ sendOdrCandidateAttorneyEmail: CandidateMutationResult }>(SEND_ATTORNEY_EMAIL);
  const [markAttorneyEmailed, { loading: markingAttorneyEmailed }] = useMutation<{ updateCandidateField: CandidateMutationResult }>(MARK_ATTORNEY_EMAILED);
  const [reviewCharges, { loading: reviewingCharges }] = useMutation<{ reviewOdrCandidateCharges: CandidateMutationResult }>(REVIEW_ODR_CANDIDATE_CHARGES);
  const [assignClinician, { loading: assigningClinician }] = useMutation<{ assignOdrCandidateClinician: CandidateMutationResult }>(ASSIGN_ODR_CANDIDATE_CLINICIAN);
  const [sendClinicianEmail, { loading: sendingClinicianEmail }] = useMutation<{ sendOdrCandidateClinicianAssignmentEmail: CandidateMutationResult }>(
    SEND_ODR_CANDIDATE_CLINICIAN_ASSIGNMENT_EMAIL,
  );
  const [completeClinicalReview, { loading: completingReview }] = useMutation<{ completeOdrCandidateClinicalReview: CandidateMutationResult }>(
    COMPLETE_ODR_CANDIDATE_CLINICAL_REVIEW,
  );
  const [saveClinicalNotes, { loading: savingNotes }] = useMutation<{ updateCandidate: CandidateMutationResult }>(SAVE_ODR_CANDIDATE_CLINICAL_NOTES);
  const [saveDemographics] = useMutation<{ updateCandidate: CandidateMutationResult }>(SAVE_ODR_CANDIDATE_DEMOGRAPHICS);
  const [createParticipant, { loading: creatingParticipant }] = useMutation<{ createOdrParticipantFromCandidate: CandidateMutationResult }>(CREATE_ODR_PARTICIPANT);
  const busy = reviewingCharges || assigningClinician || sendingClinicianEmail || completingReview || savingNotes || creatingParticipant || addingToDd || sendingAttorneyEmail || markingAttorneyEmailed;
  const needsDemographics = missingFields.includes('sex') || missingFields.includes('dob');
  const otherMissingFields = missingFields.filter((field) => !['sex', 'dob'].includes(field));
  const participantSelectionRequired = participantMatches.length > 0 && !participantChoice;

  const row = data?.candidateById;
  const candidate = useMemo(() => cellsToValues(row), [row]);
  const candidateName = candidate.name
    || [candidate.first_name, candidate.last_name].filter(Boolean).join(' ')
    || 'Candidate';
  const stage = candidate.lifecycleStage || 'received';
  const status = getOdrWorkflowPresentation(stage);
  const clinicians = cliniciansData?.odrCandidateClinicians ?? [];
  const hasParticipant = numericValue(candidate.participant) > 0;
  const taskTitle = TASK_TITLES[task];
  const attorneyEmailed = ['y', 'yes', '1', 'true'].includes((candidate.emailed_pd || '').toLowerCase());
  const taskActive = (TASK_STAGES[task].includes(stage) || (task === 'affidavit' && hasParticipant))
    && (task !== 'add-to-dd' || (attorneyEmailed && !hasParticipant));

  useEffect(() => {
    setAttorneyBody(attorneyData?.odrCandidateAttorneyEmail?.body || '');
  }, [attorneyData?.odrCandidateAttorneyEmail?.body, candidateId]);

  useEffect(() => {
    setCharges(candidate.charges || '');
    setMissingFields([]);
    setParticipantMatches([]);
    setParticipantChoice('');
  }, [candidateId, candidate.charges]);

  useEffect(() => {
    setNotes(candidate.notes || '');
  }, [candidateId, candidate.notes]);

  useEffect(() => {
    setSex(candidate.sex || '');
    setDob(candidate.dob || '');
  }, [candidateId, candidate.sex, candidate.dob]);

  useEffect(() => {
    if (clinicianUserId || !candidate.clinician || clinicians.length === 0) {
      return;
    }
    const assigned = clinicians.find((clinician) =>
      clinician.username === candidate.clinician || clinician.name === candidate.clinician
    );
    if (!assigned?.id) {
      return;
    }
    setClinicianUserId(String(assigned.id));
  }, [candidate.clinician, clinicianUserId, clinicians]);

  const finishTask = async () => {
    onOpenChange(false);
    try {
      await onCompleted();
    } catch {
      toast.error('The change was saved. Refresh the list to see the updated status.');
    }
  };

  const handleSendAttorneyEmail = async () => {
    if (busy || !attorneyBody.trim() || !attorneyData?.odrCandidateAttorneyEmail?.email) return;
    try {
      const response = await sendAttorneyEmail({ variables: { candidateId, body: attorneyBody } });
      if (!response.data?.sendOdrCandidateAttorneyEmail?.success) throw new Error('The attorney email could not be queued.');
      await finishTask();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'The attorney email could not be queued.');
    }
  };

  const handleMarkAttorneyEmailed = async () => {
    if (busy || attorneyEmailed) return;
    try {
      const response = await markAttorneyEmailed({ variables: { candidateId } });
      if (!response.data?.updateCandidateField?.success) throw new Error('The attorney email status was not saved.');
      await finishTask();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'The attorney email status was not saved.');
    }
  };

  const handleReviewCharges = async () => {
    const reviewedCharges = charges.trim();
    if (!reviewedCharges) {
      toast.error('Enter the reviewed charges before saving.');
      return;
    }
    try {
      const response = await reviewCharges({ variables: { candidateId, charges: reviewedCharges } });
      if (!response.data?.reviewOdrCandidateCharges?.success) throw new Error('The reviewed charges were not saved.');
      await finishTask();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Unable to save the reviewed charges.');
    }
  };

  const handleAssignClinician = async (sendEmail: boolean) => {
    const selectedClinicianId = Number(clinicianUserId || 0);
    if (!selectedClinicianId) {
      toast.error('Select a clinician.');
      return;
    }
    try {
      const assignment = await assignClinician({ variables: { candidateId, clinicianUserId: selectedClinicianId } });
      if (!assignment.data?.assignOdrCandidateClinician?.success) throw new Error('The clinician was not assigned.');
      if (sendEmail) {
        const notification = await sendClinicianEmail({ variables: { candidateId } });
        if (!notification.data?.sendOdrCandidateClinicianAssignmentEmail?.success) throw new Error('The clinician notification was not queued.');
        await finishTask();
        return;
      }

      await finishTask();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Unable to assign the clinician.');
    }
  };

  const handleCompleteClinicalReview = async () => {
    // Save the existing Notes field before advancing; a failed save must leave review pending.
    const notesChanged = notes !== (candidate.notes || '');
    if (notesChanged) {
      try {
        const saved = await saveClinicalNotes({ variables: { candidateId, notes } });
        if (!saved.data?.updateCandidate?.success) throw new Error('The notes were not saved.');
      } catch (error: unknown) {
        toast.error(error instanceof Error ? error.message : 'Unable to save the notes.');
        return;
      }
    }

    try {
      const response = await completeClinicalReview({ variables: { candidateId } });
      if (!response.data?.completeOdrCandidateClinicalReview?.success) throw new Error('The clinical review was not completed.');
      await finishTask();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Unable to complete the clinical review.';
      toast.error(notesChanged ? `Notes saved. ${message}` : message);
    }
  };

  const handleCreateParticipant = async () => {
    if (addingToDd) return;
    if (needsDemographics && (!sex.trim() || !dob)) {
      toast.error('Enter Sex and DOB before adding the candidate to DD.');
      return;
    }
    setAddingToDd(true);
    try {
      // Persist inline corrections before preflight reads the candidate and checks for matches.
      if (needsDemographics) {
        const saved = await saveDemographics({ variables: { candidateId, sex: sex.trim(), dob } });
        if (!saved.data?.updateCandidate?.success) throw new Error('Sex and DOB were not saved.');
      }
      const preflightResponse = await apolloClient.query<{ candidateParticipantPreflight: ParticipantPreflight }>({
        query: CANDIDATE_PARTICIPANT_PREFLIGHT,
        variables: { candidateId },
        fetchPolicy: 'network-only',
      });
      const preflight = preflightResponse?.data?.candidateParticipantPreflight;
      const nextMissingFields = Array.isArray(preflight?.missingFields) ? preflight.missingFields : [];
      setMissingFields(nextMissingFields);
      if (nextMissingFields.length > 0) {
        return;
      }

      const matches = Array.isArray(preflight?.matches) ? preflight.matches : [];
      setParticipantMatches(matches);
      const selectedMatch = matches.find((match) => String(match.participantId) === participantChoice);
      const createSeparateParticipant = participantChoice === 'new';
      if (matches.length > 0 && !selectedMatch && !createSeparateParticipant) {
        setParticipantChoice('');
        return;
      }

      const variables: { candidateId: number; acknowledgeMatches: boolean; matchedParticipantId?: number } = {
        candidateId,
        acknowledgeMatches: matches.length > 0 && createSeparateParticipant,
      };
      if (selectedMatch) {
        variables.matchedParticipantId = selectedMatch.participantId;
      }
      const response = await createParticipant({ variables });
      if (!response.data?.createOdrParticipantFromCandidate?.success) throw new Error('The candidate was not added to DD.');
      await finishTask();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Unable to add the candidate to DD.');
    } finally {
      setAddingToDd(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen: boolean) => { if (!nextOpen && busy) return; onOpenChange(nextOpen); }}>
      <DialogContent
        className="flex flex-col overflow-hidden p-0"
        contentWidth="min(1000px, 94vw)"
        contentHeight="80vh"
        contentMaxWidth="94vw"
        contentMaxHeight="80vh"
      >
        <DialogHeader className="shrink-0 border-b border-[var(--neutral-gray-40)] px-4 py-4">
          <DialogTitle className="flex items-center gap-2">
            <OdrWorkflowStatusIcon stage={stage} />
            {taskTitle} · {candidateName}
          </DialogTitle>
          <DialogDescription className="text-sm text-[var(--neutral-gray-100)]">{status.label}</DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
        {loading && <p className="py-6 text-sm text-[var(--neutral-gray-100)]">Loading task...</p>}
        {error && <p role="alert" className="py-6 text-sm text-[var(--semantic-error)]">Unable to load the candidate task.</p>}
        {!loading && !error && !row && <p className="py-6 text-sm text-[var(--semantic-error)]">Candidate not found.</p>}

        {!loading && row && taskActive && task === 'charges' && (
          <label className="flex flex-col gap-2 text-sm font-medium">
            Charges
            <Textarea
              id="odr-candidate-task-charges"
              value={charges}
              onChange={(event) => setCharges(event.target.value)}
              rows={8}
              placeholder="Enter or review charges"
            />
          </label>
        )}

        {!loading && row && taskActive && task === 'assign-clinician' && (
          <label className="flex flex-col gap-2 text-sm font-medium">
            Clinician
            <Select
              value={clinicianUserId}
              onValueChange={setClinicianUserId}
              disabled={cliniciansLoading || Boolean(cliniciansError)}
            >
              <SelectTrigger id="odr-candidate-task-clinician" aria-label="Clinician"><SelectValue placeholder="Select a clinician" /></SelectTrigger>
              <SelectContent>
                {clinicians.map((clinician) => (
                  <SelectItem id={`odr-candidate-task-clinician-${clinician.id}`} key={clinician.id} value={String(clinician.id)}>
                    {clinician.name || clinician.username}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {cliniciansError && <span role="alert" className="text-[var(--semantic-error)]">Unable to load clinicians.</span>}
          </label>
        )}

        {!loading && row && taskActive && task === 'clinical-review' && (
          <label className="flex flex-col gap-2 text-sm font-medium">
            Notes
            <Textarea
              id="odr-candidate-task-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={8}
              disabled={busy}
            />
          </label>
        )}

        {!loading && row && taskActive && task === 'attorney-email' && (
          <div className="space-y-3">
            {attorneyLoading && <p className="text-sm text-[var(--neutral-gray-100)]">Loading attorney email...</p>}
            {attorneyError && <p role="alert" className="text-sm text-[var(--semantic-error)]">Attorney email could not be loaded. Verify the referral contact and email template.</p>}
            {!attorneyLoading && !attorneyError && attorneyData?.odrCandidateAttorneyEmail && (
              <>
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="flex flex-col gap-1 text-sm font-medium">Referrer<Input id="odr-candidate-attorney-referrer" value={attorneyData.odrCandidateAttorneyEmail.referrerName} readOnly /></label>
                  <label className="flex flex-col gap-1 text-sm font-medium">Email<Input id="odr-candidate-attorney-email" type="email" value={attorneyData.odrCandidateAttorneyEmail.email} readOnly /></label>
                </div>
                <label className="flex flex-col gap-1 text-sm font-medium">Subject<Input id="odr-candidate-attorney-subject" value={attorneyData.odrCandidateAttorneyEmail.subject} readOnly /></label>
                <label className="flex flex-col gap-1 text-sm font-medium">Message<Textarea id="odr-candidate-attorney-message" value={attorneyBody} onChange={(event) => setAttorneyBody(event.target.value)} rows={10} maxLength={20000} disabled={busy} /></label>
              </>
            )}
          </div>
        )}

        {!loading && row && taskActive && task === 'add-to-dd' && !hasParticipant && (
          <div className="space-y-3">
            <p className="text-sm text-[var(--neutral-gray-120)]">
              Add this candidate to DD after reviewing any possible participant matches.
            </p>
            {otherMissingFields.length > 0 && (
              <div className="rounded-md border border-[var(--semantic-warning)] bg-[var(--semantic-warning-soft)] p-3 text-sm">
                Complete these fields before adding the client: {otherMissingFields.join(', ')}.
              </div>
            )}
            {needsDemographics && (
              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex flex-col gap-1 text-sm font-medium">
                  Sex
                  <Input id="odr-candidate-task-sex" value={sex} onChange={(event) => setSex(event.target.value)} maxLength={32} required disabled={busy} />
                </label>
                <label className="flex flex-col gap-1 text-sm font-medium">
                  DOB
                  <Input id="odr-candidate-task-dob" type="date" value={dob} onChange={(event) => setDob(event.target.value)} required disabled={busy} />
                </label>
              </div>
            )}
            {participantMatches.length > 0 && (
              <div className="space-y-2 rounded-md border border-[var(--neutral-gray-50)] p-3">
                <label className="flex flex-col gap-1 text-sm font-medium">
                  Existing participant match
                  <Select value={participantChoice} onValueChange={setParticipantChoice} disabled={busy}>
                    <SelectTrigger id="odr-candidate-task-participant-match" aria-label="Existing participant match">
                      <SelectValue placeholder="Choose an existing person or create a separate person" />
                    </SelectTrigger>
                    <SelectContent>
                      {participantMatches.map((match) => (
                        <SelectItem
                          key={`${match.program || 'Program'}-${match.participantId}`}
                          value={String(match.participantId)}
                          disabled={!match.odrParticipantId}
                        >
                          {match.program || 'Program'} participant {match.participantId}
                          {match.odrParticipantId ? ` · ODR ${match.odrParticipantId}` : ' · ODR number unavailable'}
                          {match.reason ? ` · ${match.reason}` : ''}
                        </SelectItem>
                      ))}
                      <SelectItem value="new">Create a separate person</SelectItem>
                    </SelectContent>
                  </Select>
                </label>
                <p className="text-xs text-[var(--neutral-gray-100)]">
                  Choosing an existing person keeps the same ODR number and makes the ODR Housing profile active.
                </p>
              </div>
            )}
            <Button id="odr-candidate-task-add-to-dd" type="button" onClick={() => void handleCreateParticipant()} disabled={busy || participantSelectionRequired || (needsDemographics && (!sex.trim() || !dob))}>
              <UserPlus className="h-4 w-4" aria-hidden="true" />
              {addingToDd ? 'Adding to DD...' : participantMatches.length > 0 ? 'Confirm Add to DD' : 'Add Client to DD'}
            </Button>
          </div>
        )}

        {!loading && row && taskActive && task === 'affidavit' && (
          <OdrCandidateAffidavits
            candidateId={candidateId}
            candidate={candidate}
            onChanged={async () => {
              const response = await refetch();
              await onChanged();
              const refreshedCandidate = cellsToValues(response.data?.candidateById);
              if (refreshedCandidate.lifecycleStage === 'affidavit_sent') {
                onOpenChange(false);
                await onCompleted();
              }
            }}
          />
        )}
        </div>

        {!loading && row && (
          <DialogFooter className={cn(
            'shrink-0 gap-2 border-t border-[var(--neutral-gray-40)] px-4 py-3',
            'sm:justify-end',
          )}>
            {taskActive && task === 'charges' && (
              <Button id="odr-candidate-task-save-charges" type="button" onClick={() => void handleReviewCharges()} disabled={busy || !charges.trim()}>
                <Save className="h-4 w-4" aria-hidden="true" />
                {reviewingCharges ? 'Saving...' : 'Charges Reviewed'}
              </Button>
            )}
            {taskActive && task === 'assign-clinician' && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button
                  id="odr-candidate-task-assign-only"
                  type="button"
                  variant="outline"
                  onClick={() => void handleAssignClinician(false)}
                  disabled={busy || !clinicianUserId || Boolean(cliniciansError)}
                >
                  <UserPlus className="h-4 w-4" aria-hidden="true" />
                  {assigningClinician ? 'Assigning...' : 'Assign Without Email'}
                </Button>
                <Button
                  id="odr-candidate-task-assign-and-email"
                  type="button"
                  onClick={() => void handleAssignClinician(true)}
                  disabled={busy || !clinicianUserId || Boolean(cliniciansError)}
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {assigningClinician || sendingClinicianEmail ? 'Sending...' : 'Assign & Send Secure Email'}
                </Button>
              </div>
            )}
            {taskActive && task === 'clinical-review' && (
              <Button id="odr-candidate-task-complete-review" type="button" onClick={() => void handleCompleteClinicalReview()} disabled={busy}>
                <CircleCheck className="h-4 w-4" aria-hidden="true" />
                {savingNotes || completingReview ? 'Saving...' : 'Clinician Reviewed'}
              </Button>
            )}
            {taskActive && task === 'attorney-email' && (
              <>
                <Button id="odr-candidate-task-mark-attorney-emailed" type="button" variant="outline" onClick={() => void handleMarkAttorneyEmailed()} disabled={busy || attorneyEmailed}>
                  <CircleCheck className="h-4 w-4" aria-hidden="true" />
                  {markingAttorneyEmailed ? 'Saving...' : 'Mark Attorney Emailed'}
                </Button>
                <Button id="odr-candidate-task-send-attorney-email" type="button" onClick={() => void handleSendAttorneyEmail()} disabled={busy || attorneyLoading || Boolean(attorneyError) || !attorneyBody.trim()}>
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  {sendingAttorneyEmail ? 'Sending...' : 'Send Email'}
                </Button>
              </>
            )}
            <Button id="odr-candidate-task-close" type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Close</Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
};
